import { t } from "@lingui/macro";
import {
  type CapacityAllocation,
  type CapacityAllocationItem,
  CopyCapacityAllocationItemDocument,
  type CopyCapacityAllocationItemMutation,
  type CopyCapacityAllocationItemMutationVariables,
  DeleteCapacityAllocationDocument,
  type DeleteCapacityAllocationMutation,
  type DeleteCapacityAllocationMutationVariables,
  IsDateBeforeLastPlanningUpdateDocument,
  type IsDateBeforeLastPlanningUpdateMutation,
  type IsDateBeforeLastPlanningUpdateMutationVariables,
  IsUserInTaskDocument,
  type IsUserInTaskQuery,
  type IsUserInTaskQueryVariables,
  MoveCapacityAllocationItemDocument,
  type MoveCapacityAllocationItemInput,
  type MoveCapacityAllocationItemMutation,
  type MoveCapacityAllocationItemMutationVariables,
  PlanningDailyViewWhereColumn,
  type PlanningFilterOptionsQuery,
  ReorderCapacityAllocationUserItemsDocument,
  type ReorderCapacityAllocationUserItemsMutation,
  type ReorderCapacityAllocationUserItemsMutationVariables,
  type Scalars,
  SqlOperator,
  type User,
} from "@src/__generated__/graphql";
import {
  CapacityAllocationUserItemDetailDocument,
  type CapacityAllocationUserItemDetailQuery,
  type CapacityAllocationUserItemDetailQueryVariables,
} from "@src/__generated__/urql-graphql";
import {
  PlanningDailyViewItemModel,
  type PlanningDailyViewUserByDayModel,
  type PlanningDailyViewUserDayModel,
} from "@src/components/modules/resource-planning/timeline/models";
import {
  EMPTY_FILTERS_HEIGHT,
  getCommonCapacityAllocationTypeOptions,
  USED_FILTERS_HEIGHT,
} from "@src/constants/planning";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import { client } from "@src/services/client";
import type { AppStore } from "@src/stores/AppStore";
import type { BaseStore } from "@src/stores/BaseStore";
import { PlanningViewTypeEnum } from "@src/stores/models/WorkspaceSettings";
import type {
  AffectAllocationOccurrences,
  DragItem,
  MovedAllocationPayload,
} from "@src/types/planning";
import {
  commonQueryVariables,
  commonSearchParams,
} from "@src/utils/apolloHelpers";
import { Filter, Filters } from "@src/utils/components/filters/models";
import { getTodayStart, toApiDate } from "@src/utils/dates";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import type { CSSUnitValue, Time } from "@src/utils/types";
import {
  addDays,
  eachDayOfInterval,
  endOfWeek,
  isBefore,
  isSameDay,
  startOfWeek,
} from "date-fns";
import { uniqBy } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";

type ConfirmAllocationDurationChangeModalData = {
  previousTime: Time | number;
  newTime: Time | number;
  onConfirm: () => void;
  onCancel: () => void;
};

type ConfirmSeparateAllocationModalData = {
  onConfirm: () => void | Promise<unknown>;
};

export type ResourcePlanningFetchDataArgs = {
  backgroundUpdate: boolean;
  loadForAllUsers: boolean;
};

export class ResourcePlanningViewStore implements BaseStore {
  appStore: AppStore;
  @observable selectedDate = new Date();
  @observable data: PlanningDailyViewUserByDayModel[] = [];
  @observable.ref clonedData: PlanningDailyViewUserByDayModel[] | undefined =
    undefined;
  @observable searchTerm = "";
  @observable active: DragItem | undefined = undefined;
  @observable movedAllocationPayload: MovedAllocationPayload | undefined =
    undefined;
  @observable lastSyncedAt: string | null | undefined = undefined;
  @observable syncingIds: PlanningDailyViewItemModel["id"][] = [];
  // Not observable – read by the DnD collision detection to skip collisions until the optimistic move is painted
  isSettlingDragMove = false;

  @observable
  deletingAllocationItemId = new Set<CapacityAllocation["id"]>();

  private deleteAllocationMutator = new MutationHelper<
    DeleteCapacityAllocationMutation,
    DeleteCapacityAllocationMutationVariables
  >(DeleteCapacityAllocationDocument);

  confirmDurationChangeModal =
    new DisclosureState<ConfirmAllocationDurationChangeModalData>();

  confirmSeparateAllocationModal =
    new DisclosureState<ConfirmSeparateAllocationModalData>();

  where: Filters<PlanningDailyViewWhereColumn>;

  userInTaskFetcher = new FetchHelper<
    IsUserInTaskQuery,
    IsUserInTaskQueryVariables
  >(IsUserInTaskDocument);

  moveAllocationMutator = new MutationHelper<
    MoveCapacityAllocationItemMutation,
    MoveCapacityAllocationItemMutationVariables
  >(MoveCapacityAllocationItemDocument);
  copyAllocationMutator = new MutationHelper<
    CopyCapacityAllocationItemMutation,
    CopyCapacityAllocationItemMutationVariables
  >(CopyCapacityAllocationItemDocument);
  reorderAllocationMutator = new MutationHelper<
    ReorderCapacityAllocationUserItemsMutation,
    ReorderCapacityAllocationUserItemsMutationVariables
  >(ReorderCapacityAllocationUserItemsDocument);
  syncCheckMutation = new MutationHelper<
    IsDateBeforeLastPlanningUpdateMutation,
    IsDateBeforeLastPlanningUpdateMutationVariables
  >(IsDateBeforeLastPlanningUpdateDocument);

  @observable _fetchData: (
    loadForAllUsers: boolean,
    silent: boolean,
  ) => Promise<any>;

  constructor(
    appStore: AppStore,
    fetchData: (loadForAllUsers: boolean, silent: boolean) => Promise<any>,
    onFilterChange?: () => void,
  ) {
    makeObservable(this);
    this._fetchData = fetchData;
    this.appStore = appStore;
    this.where = new Filters(
      [
        new Filter({
          column: PlanningDailyViewWhereColumn.TeamId,
          operator: SqlOperator.In,
          title: t`Team`,
          options: [],
          hidden: true,
        }),
        new Filter({
          column: PlanningDailyViewWhereColumn.TimeTrackingWorkTypeId,
          operator: SqlOperator.In,
          title: t`Position`,
          options: [],
        }),
        new Filter({
          column: PlanningDailyViewWhereColumn.AllocableType,
          operator: SqlOperator.In,
          title: t`Type`,
          options: getCommonCapacityAllocationTypeOptions(),
        }),
        new Filter({
          column: PlanningDailyViewWhereColumn.TaskPriorityId,
          operator: SqlOperator.In,
          title: t`Priority`,
          options: [],
          isCollapsed: true,
        }),
        new Filter({
          column: PlanningDailyViewWhereColumn.TaskStatusId,
          operator: SqlOperator.In,
          title: t`Task status`,
          options: [],
          isCollapsed: true,
        }),
        new Filter({
          column: PlanningDailyViewWhereColumn.BrandId,
          operator: SqlOperator.In,
          title: t`Client/Brand`,
          tagTitle: t`Brand`,
          options: [],
          sectioned: true,
          hasSelectAllOption: true,
          isCollapsed: true,
        }),
        new Filter({
          column: PlanningDailyViewWhereColumn.UserId,
          title: t`User`,
          operator: SqlOperator.In,
          options: [],
          isCollapsed: true,
        }),
        new Filter({
          column: PlanningDailyViewWhereColumn.CreatedByUserId,
          operator: SqlOperator.In,
          title: t`Creator`,
          options: [],
          isCollapsed: true,
        }),
      ],
      {
        onChange: () => {
          onFilterChange?.();
          this.fetchDailyView();
        },
        tableKey: "resource-planning",
      },
    );
  }

  @computed get searchParams() {
    return commonSearchParams(this);
  }
  @computed get queryParams() {
    return commonQueryVariables(this);
  }

  @computed get selectedDateRange(): [Date, Date] {
    if (
      this.appStore.workspaceStore.settings?.default_planning_view_settings
        .view_type === PlanningViewTypeEnum.WEEK_FROM_NOW
    ) {
      return [this.selectedDate, addDays(this.selectedDate, 6)];
    } else {
      const opts = {
        weekStartsOn: this.appStore.workspaceStore.settings?.startOfWeekNumber,
      };
      return [
        startOfWeek(this.selectedDate, opts),
        endOfWeek(this.selectedDate, opts),
      ];
    }
  }

  @computed get daysOfSelectedInterval(): Date[] {
    const [start, end] = this.selectedDateRange;
    return eachDayOfInterval({
      start,
      end,
    });
  }

  @computed get filterRowHeight(): CSSUnitValue {
    const filtersUsed = this.where.filters.some(({ column, value, hidden }) => {
      if (column === PlanningDailyViewWhereColumn.UserId && hidden)
        return false;
      return value.length;
    });

    return filtersUsed ? USED_FILTERS_HEIGHT : EMPTY_FILTERS_HEIGHT;
  }

  @action setFilterOptions(data: PlanningFilterOptionsQuery) {
    this.where.filtersByColumn
      .get(PlanningDailyViewWhereColumn.TaskStatusId)
      ?.setOptions(
        data.taskStatuses.map(({ id, name }) => ({
          value: id,
          label: name,
        })),
      );

    this.where.filtersByColumn
      .get(PlanningDailyViewWhereColumn.TaskPriorityId)
      ?.setOptions(
        data.taskPriorities.map(({ id, name }) => ({
          value: id,
          label: name,
        })),
      );

    this.where.filtersByColumn
      .get(PlanningDailyViewWhereColumn.BrandId)
      ?.setOptions(
        data.clientsSimpleMap.map((i) => ({
          value: i?.id,
          label: i?.name,
          options: i?.brands.map(({ name, id }) => {
            return {
              label: name,
              value: id,
            };
          })!,
        })),
      );

    this.where.filtersByColumn
      .get(PlanningDailyViewWhereColumn.UserId)
      ?.setOptions(
        data.userSimpleMap.map(({ id, full_name }) => ({
          value: id,
          label: full_name,
        })),
      );

    this.where.filtersByColumn
      .get(PlanningDailyViewWhereColumn.CreatedByUserId)
      ?.setOptions(
        data.userSimpleMap.map(({ id, full_name }) => ({
          value: id,
          label: full_name,
        })),
      );

    this.where.filtersByColumn
      .get(PlanningDailyViewWhereColumn.TimeTrackingWorkTypeId)
      ?.setOptions(
        data.timeTrackingWorkTypes.map(({ id, title }) => ({
          value: id,
          label: title,
        })),
      );

    this.where.filtersByColumn
      .get(PlanningDailyViewWhereColumn.TeamId)
      ?.setOptions(
        data.teamSimpleMap.map(({ id, name }) => ({
          value: id,
          label: name,
        })),
      );
  }

  @action.bound async fetchDailyView(
    { backgroundUpdate, loadForAllUsers }: ResourcePlanningFetchDataArgs = {
      backgroundUpdate: false,
      loadForAllUsers: false,
    },
  ) {
    await this._fetchData(loadForAllUsers, backgroundUpdate);
  }

  @action.bound async syncData() {
    await this.fetchDailyView({
      backgroundUpdate: true,
      loadForAllUsers: true,
    });
  }

  @action.bound optimisticallyMoveAllocationItem({
    from_date,
    to_date,
    to_user_id,
    from_user_id,
    userItem,
    newIndex,
    oldIndex,
  }: Omit<
    MovedAllocationPayload,
    "current_date" | "current_user_id" | "current_index"
  >) {
    const { current_date, current_user_id, current_index } =
      this.movedAllocationPayload ?? {};
    const fromDateString = from_date.toDateString();
    const toDateString = to_date.toDateString();
    const currentDateString = current_date?.toDateString();

    // If the allocation item is dropped in the same place, do nothing
    if (
      !current_date &&
      !current_user_id &&
      fromDateString === toDateString &&
      from_user_id === to_user_id &&
      oldIndex === newIndex
    )
      return;

    // If the allocation item is already at the target position, do nothing
    if (
      currentDateString === toDateString &&
      current_user_id === to_user_id &&
      current_index === newIndex
    )
      return;

    this.isSettlingDragMove = true;
    requestAnimationFrame(() => {
      this.isSettlingDragMove = false;
    });

    const dataLength = this.data.length;
    for (let i = 0; i < dataLength; i++) {
      const assigneeItem = this.data[i];
      const userId = assigneeItem.user.id;

      if (
        // If user is not relevant to the move, skip
        userId !== from_user_id &&
        userId !== to_user_id &&
        userId !== current_user_id
      ) {
        continue;
      }

      const newAssigneeItems: PlanningDailyViewUserDayModel[] = [];
      const assigneeItemsCount = assigneeItem.items.length;

      for (let j = 0; j < assigneeItemsCount; j++) {
        const day = assigneeItem.items[j];
        const dateString = new Date(day.date).toDateString();

        if (
          // If day is not relevant to the move, skip
          dateString !== fromDateString &&
          dateString !== toDateString &&
          dateString !== currentDateString
        ) {
          newAssigneeItems.push(day);
          continue;
        }

        const items: PlanningDailyViewItemModel[] = [];
        const dayItemCount = day.items.length;

        for (let k = 0; k < dayItemCount; k++) {
          const item = day.items[k];
          if (item.id === userItem.id) continue;
          items.push(item);
        }

        // Add or/and reorder item
        if (isSameDay(day.date, to_date) && userId === to_user_id) {
          this.updateCurrentMovedAllocationPayload({
            current_date: to_date,
            current_user_id: to_user_id,
            current_index: newIndex,
          });
          items.splice(newIndex, 0, userItem);
          day.items = items;
        } else {
          // Remove item from old place
          day.items = items;
        }

        newAssigneeItems.push(day);
      }

      assigneeItem.items = newAssigneeItems;
    }
  }

  private validateMove(payload: MovedAllocationPayload) {
    const { to_user_id, from_user_id, from_date, to_date, oldIndex, newIndex } =
      payload;

    const fromDateString = from_date.toDateString();
    const toDateString = to_date.toDateString();
    const movedToSameDay = fromDateString === toDateString;
    const movedToSameUser = from_user_id === to_user_id;
    const movedToSameUserAndDay = movedToSameDay && movedToSameUser;
    const onlyReordered = movedToSameUserAndDay && oldIndex !== newIndex;
    const itemNotMoved = movedToSameUserAndDay && oldIndex === newIndex;

    return {
      onlyReordered,
      itemNotMoved,
      movedToSameDay,
      movedToSameUser,
      movedToSameUserAndDay,
    };
  }

  @action.bound async moveAllocationItem(
    movePayload: MovedAllocationPayload,
    newWorkTypeId?: MoveCapacityAllocationItemInput["new_time_tracking_work_type_id"],
  ) {
    const { id, to_user_id, from_user_id, to_date, userItem } = movePayload;

    const { itemNotMoved, onlyReordered, movedToSameDay, movedToSameUser } =
      this.validateMove(movePayload);

    // If the allocation item is dropped in the same place, do nothing
    if (itemNotMoved) {
      return;
    }

    this.syncingIds.push(userItem.id);

    if (!onlyReordered) {
      const [data, error] = await this.moveAllocationMutator.mutate({
        input: {
          id,
          to_user_id,
          from_user_id,
          date: toApiDate(to_date),
          new_time_tracking_work_type_id: newWorkTypeId,
        },
      });

      if (error) {
        this.syncingIds = this.syncingIds.filter(
          (syncId) => syncId !== userItem.id,
        );
        return this.syncData();
      }

      if (data?.moveCapacityAllocationItem) {
        if (!movedToSameUser || !movedToSameDay) {
          this.appStore.UIStore.toast({
            status: "warning",
            title: t`This allocation will be disconnected from the series and will require separate editing.`,
          });
        }
        this.locallyUpdateItemsAfterMove({
          userItems: data.moveCapacityAllocationItem.userItems,
          movePayload,
        });
      }
    }

    this.reorderAllocationItems(movePayload);
  }

  @action.bound async duplicateAllocationItem(
    payload: MovedAllocationPayload,
    newWorkTypeId?: MoveCapacityAllocationItemInput["new_time_tracking_work_type_id"],
  ) {
    const { to_user_id, from_user_id, to_date, userItem, newIndex } = payload;
    const { itemNotMoved, onlyReordered } = this.validateMove(payload);

    if (itemNotMoved || onlyReordered) return;

    const dataCount = this.data.length;
    for (let i = 0; i < dataCount; i++) {
      const item = this.data[i];
      if (!item) continue;
      if (item.user.id !== from_user_id) continue;

      const itemCount = item.items.length;

      for (let l = 0; l < itemCount; l++) {
        const userItem = item.items[l];

        if (
          new Date(userItem.date).toDateString() !==
          payload.from_date.toDateString()
        )
          continue;

        userItem.items.splice(payload.oldIndex, 0, payload.userItem);
      }
    }

    this.syncingIds.push(userItem.id);

    const [data, error] = await this.copyAllocationMutator.mutate({
      input: {
        id: userItem.id,
        to_user_id: to_user_id,
        from_user_id: from_user_id,
        date: toApiDate(to_date),
        new_time_tracking_work_type_id: newWorkTypeId,
      },
    });

    if (error) {
      this.syncData();
      this.syncingIds = this.syncingIds.filter((id) => id !== userItem.id);
      return;
    }

    const matchingUserItem = this.data.find(
      ({ user }) => user.id === to_user_id,
    );

    if (!matchingUserItem) return;
    const resUserItem = data?.copyCapacityAllocationItem;
    if (!resUserItem) return;
    const itemCount = matchingUserItem.items.length;

    for (let i = 0; i < itemCount; i++) {
      const userItem = matchingUserItem.items[i];
      if (new Date(userItem.date).toDateString() !== to_date.toDateString())
        continue;
      userItem.items.splice(
        newIndex,
        1,
        new PlanningDailyViewItemModel(resUserItem, resUserItem.user),
      );
    }

    this.appStore.UIStore.toast({
      status: "warning",
      title: t`This allocation will be disconnected from the series and will require separate editing.`,
    });

    this.syncingIds = [];
  }

  public async checkIfUserInTask(
    userId: Scalars["ID"]["output"],
    taskId: Scalars["ID"]["output"],
  ) {
    const [data] = await this.userInTaskFetcher.fetch({
      userId,
      taskId,
    });

    return data?.isUserInTask;
  }

  @action.bound async reorderAllocationItems({
    userItem,
    to_date,
    to_user_id,
  }: MovedAllocationPayload) {
    const ids =
      this.data
        .find((assigneeItem) => assigneeItem.user.id === to_user_id)
        ?.items.find((day) => isSameDay(new Date(day.date), to_date))
        ?.items?.map(({ id }) => id) ?? [];

    const [, error] = await this.reorderAllocationMutator.mutate({
      ids,
    });

    this.syncingIds = this.syncingIds.filter(
      (syncId) => syncId !== userItem.id,
    );

    if (error) {
      this.syncData();
    }
  }

  @action.bound locallyUpdateItemsAfterMove({
    userItems,
    movePayload,
  }: {
    userItems: MoveCapacityAllocationItemMutation["moveCapacityAllocationItem"]["userItems"];
    movePayload: MovedAllocationPayload;
  }): void {
    const { to_date, to_user_id, userItem } = movePayload;

    userItems.forEach((updatedUserItem) => {
      let foundUserItem = this.findUserItem({
        id: updatedUserItem.id,
        userId: updatedUserItem.user.id,
        date: new Date(updatedUserItem.date),
      });

      // If the item was moved, we need to find it in the new place because ID could be changed
      if (
        to_user_id === updatedUserItem.user.id &&
        to_date.toDateString() === new Date(updatedUserItem.date).toDateString()
      ) {
        foundUserItem = this.findMovedUserItem({
          id: userItem.id,
          to_user_id,
          to_date,
        });
      }

      if (foundUserItem) {
        foundUserItem.completed = updatedUserItem.completed;
        foundUserItem.user = updatedUserItem.user;
        foundUserItem.time_tracking_work_type_id =
          updatedUserItem.time_tracking_work_type_id;
        foundUserItem.id = updatedUserItem.id;
      }
    });
  }

  async checkIfSyncNeeded() {
    const [data] = await this.syncCheckMutation.mutate({
      date: this.lastSyncedAt,
    });

    if (data?.isDateBeforeLastPlanningUpdate) {
      this.syncData();
    }
  }

  updateMovedAllocationPayload(
    payload: Omit<
      MovedAllocationPayload,
      "current_date" | "current_user_id" | "current_index"
    >,
  ) {
    this.movedAllocationPayload = {
      current_date: this.movedAllocationPayload?.current_date,
      current_user_id: this.movedAllocationPayload?.current_user_id,
      current_index: this.movedAllocationPayload?.current_index,
      to_user_id: payload.to_user_id,
      id: payload.id,
      to_date: payload.to_date,
      newIndex: payload.newIndex,
      oldIndex: payload.oldIndex,
      userItem: payload.userItem,
      from_date: payload.from_date,
      from_user_id: payload.from_user_id,
    };
  }

  updateCurrentMovedAllocationPayload(
    payload: Pick<
      MovedAllocationPayload,
      "current_date" | "current_user_id" | "current_index"
    >,
  ) {
    if (!this.movedAllocationPayload) return;
    this.movedAllocationPayload = {
      current_date: payload.current_date,
      current_index: payload.current_index,
      current_user_id: payload.current_user_id,
      id: this.movedAllocationPayload.id,
      userItem: this.movedAllocationPayload.userItem,
      from_user_id: this.movedAllocationPayload.from_user_id,
      from_date: this.movedAllocationPayload.from_date,
      oldIndex: this.movedAllocationPayload.oldIndex,
      newIndex: this.movedAllocationPayload.newIndex,
      to_date: this.movedAllocationPayload.to_date,
      to_user_id: this.movedAllocationPayload.to_user_id,
    };
  }

  @action.bound revertChanges(): void {
    if (this.clonedData?.length) {
      this.data = this.clonedData;
    }
    this.clonedData = undefined;
  }

  /**
   * @param [deleteItemsAfterDeleteDate=true] default = true
   */
  locallyDeleteCapacityAllocation(
    capacityAllocationId: CapacityAllocation["id"],
    deleteDate?: CapacityAllocationItem["date"],
    deleteItemsAfterDeleteDate = true,
  ) {
    if (deleteDate) {
      this.data.forEach((assigneeItem) => {
        assigneeItem.items.forEach((day) => {
          day.items = day.items.filter(({ stats, date }) => {
            return (
              (deleteItemsAfterDeleteDate
                ? isBefore(date, deleteDate)
                : !isSameDay(date, deleteDate)) ||
              stats?.capacity_allocation_id !== capacityAllocationId
            );
          });
        });
      });
    } else {
      this.data.forEach((assigneeItem) => {
        assigneeItem.items.forEach((day) => {
          day.items = day.items.filter(({ stats }) => {
            return stats?.capacity_allocation_id !== capacityAllocationId;
          });
        });
      });
    }
  }

  locallyDeleteAllocationItem(id: CapacityAllocationItem["id"]) {
    this.data.forEach((assigneeItem) => {
      assigneeItem.items.forEach((day) => {
        day.items = day.items.filter((item) => item.id !== id);
      });
    });
  }

  findUserItem(item: {
    id: PlanningDailyViewItemModel["id"];
    userId: User["id"];
    date: Date;
  }): PlanningDailyViewItemModel | undefined {
    return this.data
      .find((assigneeItem) => assigneeItem.user.id === item.userId)
      ?.items.find(
        (day) => new Date(day.date).toDateString() === item.date.toDateString(),
      )
      ?.items.find(({ id }) => id === item.id);
  }

  findMovedUserItem(movedItem: {
    id: PlanningDailyViewItemModel["id"];
    to_user_id: User["id"];
    to_date: Date;
  }): PlanningDailyViewItemModel | undefined {
    return this.data
      .find((assigneeItem) => assigneeItem.user.id === movedItem.to_user_id)
      ?.items.find(
        (day) =>
          new Date(day.date).toDateString() ===
          movedItem.to_date.toDateString(),
      )
      ?.items.find(({ id }) => id === movedItem.id);
  }

  @action.bound
  async declineImportedAllocation(
    id: CapacityAllocation["id"],
    declineForUserId?: User["id"],
  ) {
    this.deletingAllocationItemId.add(id);

    const [, error] = await this.deleteAllocationMutator.mutate({
      input: {
        id,
        ...(declineForUserId ? { decline_for_user_id: declineForUserId } : {}),
      },
    });

    this.deletingAllocationItemId.delete(id);
    if (error) return;

    this.locallyDeleteAllocationItem(id);
    this.syncData();
  }

  @action.bound
  declineImportedAllocationForUser(
    id: CapacityAllocation["id"],
    userId: User["id"],
  ) {
    this.confirmSeparateAllocationModal.onOpen({
      onConfirm: () => this.declineImportedAllocation(id, userId),
    });
  }

  @action.bound
  acceptImportedAllocationForUser(item: PlanningDailyViewItemModel) {
    this.confirmSeparateAllocationModal.onOpen({
      onConfirm: () =>
        this.handleEdit(item, "currentUser", undefined, item.user.id),
    });
  }

  @action.bound
  async handleEdit(
    item: PlanningDailyViewItemModel,
    affectOccurrences: AffectAllocationOccurrences,
    handleChangeLoading?: (isLoading: boolean) => void,
    acceptForUserId?: User["id"],
  ) {
    if (handleChangeLoading) {
      handleChangeLoading(true);
    } else {
      this.deletingAllocationItemId.add(item.id);
    }

    const { data } = await client
      .query<
        CapacityAllocationUserItemDetailQuery,
        CapacityAllocationUserItemDetailQueryVariables
      >(CapacityAllocationUserItemDetailDocument, {
        id: item.id,
        today: getTodayStart(),
      })
      .toPromise();

    if (handleChangeLoading) {
      handleChangeLoading(false);
    } else {
      this.deletingAllocationItemId.delete(item.id);
    }

    if (!data?.capacityAllocationItemDetailQuery) {
      this.appStore.UIStore.toast({
        status: "error",
        title: t`Failed to fetch allocation details`,
      });
      return;
    }

    this.appStore.allocationModalStore.modalState.onOpen({
      affectOccurrences,
      acceptForUserId,
      formData: {
        id:
          affectOccurrences === "allOccurrences" ||
          affectOccurrences === "currentUser"
            ? item.stats?.capacity_allocation_id
            : item.stats?.capacity_allocation_item_id,
        item: data?.capacityAllocationItemDetailQuery,
        assignees: uniqBy(
          data.capacityAllocationItemDetailQuery.userItems,
          "user.id",
        ),
        tracked_for_item: item.tracked_for_item,
      },
      onSubmit: () =>
        this.fetchDailyView({
          backgroundUpdate: false,
          loadForAllUsers: true,
        }),
    });
  }
}
